import saveAs from "file-saver";
import config from "../config.json";
import {
  IBaseConnectionProfile,
  IPageable,
  Params,
} from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import qs from "qs";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export interface IAccountFieldNameLocation {
  name: string;
  location: "URL" | "BODY" | "HEADER";
}

export interface ISendRequestField {
  sendApiKey?: IAccountFieldNameLocation;
  username?: IAccountFieldNameLocation;
  password?: IAccountFieldNameLocation;
  destination?: IAccountFieldNameLocation;
  source?: IAccountFieldNameLocation;
  text?: IAccountFieldNameLocation;
  dataCoding?: IAccountFieldNameLocation;
  httpMethod?: "GET" | "POST";
  contentType?: string;
}

export interface ISendResponseField {
  errorCode?: string;
  errorDescription?: string;
  messageErrorCode?: string;
  messageId?: string;
  originatingAddress?: string;
  destinationAddress?: string;
  messageCount?: string;
  messageParts?: string;
}

export interface IDlrRequestField {
  dlrApiKey?: IAccountFieldNameLocation;
  sender?: IAccountFieldNameLocation;
  destination?: IAccountFieldNameLocation;
  messageId?: IAccountFieldNameLocation;
  deliveryStatus?: IAccountFieldNameLocation;
  dateReceived?: IAccountFieldNameLocation;
  description?: IAccountFieldNameLocation;
  username?: IAccountFieldNameLocation;
  password?: IAccountFieldNameLocation;
  httpMethod?: string;
  contentType?: string;
}

export interface IDlrResponseField {
  errorCode?: string;
  errorDescription?: string;
}

export interface IAccount extends IBaseConnectionProfile {
  id: string;
  user?: string;
  accountManager?: string;
  tpsHardLimit?: number;
  windowSize?: number;
  hardTps: string;
  bind?: number;
  isUp?: boolean;
  updatedBy?: string;
  openBinds?: string;
  acceptedIps?: string;
  lastBindError?: string;
  connectionInterface?: "SMPP" | "HTTP";
  interfaces?: string[];
  httpUsername?: string;
  httpPassword?: string;
  sendBaseUrl?: string;
  sendApiKey?: string;
  dlrBaseUrl?: string;
  dlrApiKey?: string;
  sendRequestField?: ISendRequestField;
  sendResponseField?: ISendResponseField;
  dlrRequestField?: IDlrRequestField;
  dlrResponseField?: IDlrResponseField;
  smppScheduleDeliveryTime?: string;
  ratePerDelivery?: boolean;
  costPerDelivery?: boolean;
}
export const exportAccount = async (
  params: Params<IAccount> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/connection-profiles/csv", {
      params,
      responseType: "blob",
    });

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, "Account.csv");

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching Account:",
      error.response?.data?.message || error.message || error
    );
  }
};

export async function fetchAccounts(
  params: Params<IAccount>
): Promise<IPageable<IAccount>> {
  try {
    const { name, ...paramsWithoutName } = params;
    const response = await axiosInstance.get("/connection-profiles/advanced", {
      params: { ...paramsWithoutName, search: params.search ?? "" },
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat", encode: false }),
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("The requested resource was not found.");
    }
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching accounts."
    );
  }
}
export async function fetchAccountsAll(
  params: Params<IAccount>
): Promise<IPageable<IAccount>> {
  try {
    const { name, ...paramsWithoutName } = params;
    const response = await axiosInstance.get("/connection-profiles/all", {
      params: { ...paramsWithoutName, search: params.search ?? "" },
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat", encode: false }),
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("The requested resource was not found.");
    }
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching accounts."
    );
  }
}
export async function fetchAccountsNA(
  params: Params<IAccount>
): Promise<IPageable<IAccount>> {
  try {
    const { name, ...paramsWithoutName } = params;
    const response = await axiosInstance.get("/connection-profiles", {
      params: { ...paramsWithoutName, search: params.search ?? "" },
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat", encode: false }),
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("The requested resource was not found.");
    }
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching accounts."
    );
  }
}

export async function fetchAccount(id: IAccount["id"]): Promise<IAccount> {
  try {
    const response = await axiosInstance.get(`/connection-profiles/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting Account/${id}.`
    );
  }
}
export async function fetchSessions(id: IAccount["id"]): Promise<IAccount> {
  try {
    const response = await axiosInstance.get(`/session-logs/text`, {
      params: { connectionProfileId: id },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting Account/${id}.`
    );
  }
}
export const addAccountApi = async (data: Partial<IAccount>) => {
  try {
    const response = await axiosInstance.post(
      `/connection-profiles/clients`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the account."
    );
  }
};

export const editAccountApi = async (id: string, data: Partial<IAccount>) => {
  try {
    const response = await axiosInstance.put(
      `/connection-profiles/clients/${id}`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while updating the account.`
    );
  }
};

export const getAccountById = async (
  id: string,
  type: "VENDOR" | "CLIENT"
): Promise<IAccount> => {
  try {
    const response = await axiosInstance.get(`/connection-profiles/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while fetching the ${type.toLowerCase()} account.`
    );
  }
};

export const deleteAccountApi = async (id: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/connection-profiles/clients/${id}`);
    console.log(`Account with ID ${id} deleted successfully.`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while deleting the account.`
    );
  }
};
export const deleteBindsApi = async (id: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/connection-profiles/${id}/binds`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while deleting the Binds.`
    );
  }
};

export const useSessionQuery = (
  id: string
): UseQueryResult<IPageable<IAccount>, Error> => {
  const queryKey = ["accounts", id];
  return useQuery({
    queryKey,
    queryFn: () => fetchSessions(id),
    refetchInterval: 2000,
  });
};

export const useAccountsQuery = (
  params: Params<IAccount>
): UseQueryResult<IPageable<IAccount>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["accounts", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchAccounts(effectiveParams),
  });
};
export const useAccountsQueryAll = (
  params: Params<IAccount>
): UseQueryResult<IPageable<IAccount>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["accounts", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchAccountsAll(effectiveParams),
  });
};


export const useAccountsQueryNA = (
  params: Params<IAccount>
): UseQueryResult<IPageable<IAccount>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["accounts", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchAccountsNA(effectiveParams),
  });
};

export const getAccountOptions = (customerId: string) => ({
  queryKey: ["accounts", customerId],
  enabled:
    Boolean(customerId) &&
    customerId !== "undefined" &&
    customerId !== "null",
  initialData: "N/A",
  queryFn: async (): Promise<string> => {
    if (
      !customerId ||
      customerId === "undefined" ||
      customerId === "null"
    ) {
      return "N/A";
    }
    try {
      const client = await fetchAccount(customerId);
      return client?.name;
    } catch (error: any) {
      return "Error fetching customer";
    }
  },
  select: (name: string) => name,
});

export const useAccountQuery = (
  params: { name: string },
  options?: UseQueryOptions<IPageable<IAccount>, Error>
): UseQueryResult<IPageable<IAccount>, Error> => {
  const queryKey = ["accounts", JSON.stringify(params)];
  return useQuery({
    queryKey,
    queryFn: () => fetchAccounts(params),
    ...options,
  });
};

export const exportSessionLogs = async (
  params: Params<IAccount & { connectionProfileId: number }> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/session-state-logs/csv", {
      params,
      responseType: "blob",
    });

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, "session-state-logs.csv");

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching session-state-logs:",
      error.response?.data?.message || error.message || error
    );
  }
};

export async function fetchAccountHistory(
  id: IAccount["id"],
  params: Params<IAccount> = {}
): Promise<IPageable<IAccount>> {
  try {
    const response = await axiosInstance.get(`/connection-profiles/history`, {
      params: { mainId: id, sort: "historyTimestamp,desc", ...params },
    });
    const data = response.data;
    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp,
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the FallBack Routing history for ID ${id}.`
    );
  }
}

export const getAccountNamesByProfile = (profileId: string) => ({
  queryKey: ["accounts", profileId],
  queryFn: async (): Promise<string[]> => {
    const clientsPage = await fetchAccountsNA({
      profileId,
    } as Params<IAccount>);
    if (clientsPage.content && clientsPage.content.length > 0) {
      return clientsPage.content.map((client) => client.name);
    }
    return [];
  },
  select: (names: string[]) => names,
});

export const useAccountByProfileIdQuery = (
  profileId: string,
  options?: UseQueryOptions<string[], Error>
): UseQueryResult<string[], Error> => {
  return useQuery({
    ...getAccountNamesByProfile(profileId),
    ...options,
  });
};
